<template>
    <div :class="[prefixCls, prefixCls + '-table']">
        <!-- left fixed -->
        <div v-if="leftColumns.length > 0"
            :class="[prefixCls + '-table-fixed-left']">
            <tree-table-head v-if="showHeader"
                ref="leftHeader"
                location="left"
                :columns="leftColumns">
            </tree-table-head>
            <div ref="leftContent"
                v-if="stateTree.length > 0"
                :class="prefixCls + '-table-content'"
                :style="leftContentStyle"
                @mousewheel="handleFixedBodyMousewheel"
                @DOMMouseScroll="handleFixedBodyMousewheel">
                <tree-table-body ref="leftBody"
                    location="left"
                    :stateTree="stateTree"
                    :columns="leftColumns">
                </tree-table-body>
            </div>
        </div>
        <!-- middle -->
        <tree-table-head v-if="showHeader"
            ref="header"
            location="middle"
            :width="tableWidth"
            :columns="flatColumns"
            :showVerticalScrollBar="showVerticalScrollBar">
        </tree-table-head>
        <div ref="content"
            v-if="stateTree.length > 0"
            :class="prefixCls + '-table-content'"
            :style="contentStyle"
            @scroll="handleBodyScroll">
            <tree-table-body ref="body"
                :stateTree="stateTree"
                :columns="flatColumns">
            </tree-table-body>
        </div>
        <!-- no-data -->
        <div v-else
            :class="[prefixCls + '-table-tip']"
            :style="{height: contentHeight}"
            @scroll="handleBodyScroll">
            <table style="table-layout: fixed; width: 100%;"
                cellspacing="0"
                cellpadding="0"
                border="0">
                <tbody>
                    <tr>
                        <td :style="{'height': contentHeight, 'width':`${this.contentWidth}px`}">
                            <div :style="noDataImgStyle">
                                <span v-html="localeNoDataText"></span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- right fixed -->
        <div v-if="rightColumns.length > 0"
            :class="[prefixCls + '-table-fixed-right']">
            <tree-table-head v-if="showHeader"
                ref="rightHeader"
                location="right"
                :columns="rightColumns">
            </tree-table-head>
            <div ref="rightContent"
                v-if="stateTree.length > 0"
                :class="prefixCls + '-table-content'"
                :style="rightContentStyle"
                @mousewheel="handleFixedBodyMousewheel"
                @DOMMouseScroll="handleFixedBodyMousewheel">
                <tree-table-body ref="rightBody"
                    location="right"
                    :stateTree="stateTree"
                    :columns="rightColumns">
                </tree-table-body>
            </div>
        </div>
        <!-- global -->
        <Spin fix
            size="large"
            v-if="loading">
            <slot name="loading"></slot>
        </Spin>
        <Tooltip transfer
            ref="cellTooltip"
            style="display: none;"
            v-bind="tipProps">
            <div slot="content"
                style="word-wrap: break-word; word-break: break-all; white-space: normal;">
                {{tipProps.content}}
            </div>
        </Tooltip>
    </div>
</template>
<script>
import Spin from '../spin'
import TreeTableHead from './tree-table-head.vue'
import TreeTableBody from './tree-table-body.vue'
import Tooltip from '../tooltip'
import elementResizeDetectorMaker from 'element-resize-detector'
import { debounce, getScrollBarSize, now, deepCopy } from '../../utils/assist'
import Emitter from '../../mixins/emitter'
import { on, off } from '../../utils/dom'
import Locale from '../../mixins/locale'
import TableFormula from '../../mixins/table-formula'
import TableTrack from '../../mixins/table-track'
import TableExport from '../../mixins/table-export'

const defaults = {
    noDataText: void(0),
    noDataImg: void(0),
}

export default {
    name: 'TreeTable',
    mixins: [Locale, Emitter, TableFormula, TableTrack, TableExport],
    components: {
        Spin,
        Tooltip,
        TreeTableHead,
        TreeTableBody,
    },
    props: {
        name: String,
        data: {
            type: Array,
            default () {
                return []
            }
        },
        multiple: {
            type: Boolean,
            default: false
        },
        showCheckbox: {
            type: Boolean,
            default: false
        },
        // 是否显示表头
        showHeader: {
            type: Boolean,
            default: true
        },
        noDataText: {
            type: String,
            default: () => defaults.noDataText
        },
        noDataImg: {
            type: String,
            default: () => defaults.noDataImg
        },
        childrenKey: {
            type: String,
            default: 'children'
        },
        loadData: {
            type: Function
        },
        render: {
            type: Function
        },
        // 取消check级联效果
        isolation: {
            type: Boolean,
            default: false
        },
        columns: {
            type: Array,
            default () {
                return []
            }
        },
        height: {
            type: [Number, String],
            default: "auto"
        },
        draggable: {
            type: Boolean,
            default: false
        },
        // 折叠展开触发点配置
        expander: {
            type: [String, Function],
            default: "cell",
            validator: function(value) {
                if (typeof value == "function") {
                    return true
                }

                // 这个值必须匹配下列字符串中的一个
                return ['icon', 'cell', 'row'].indexOf(value) !== -1
            }
        },
        expandLevel: {
            type: Number,
            default: 2
        },
        highlightRow: {
            type: Boolean,
            default: false
        },
        rowClassName: {
            type: String,
            default: ""
        },
        // 行背景色设置
        background: {
            type: [Function, Array],
        },
        loading: {
            type: Boolean,
            default: false
        },
        // 编辑模式
        editMode: {
            type: String,
            validator(val) {
                // 只读、行编辑、全网格编辑
                return ["readonly", "row", "grid"].indexOf(val) >= 0
            },
            default: "readonly"
        },
    },
    data() {
        return {
            stateTree: this.data,
            flatState: [],
            // 滚动条宽度
            scrollBarWidth: getScrollBarSize(),
            // resize 观测
            observer: null,
            // rect
            tableWidth: 0,
            headerHeight: 40,
            contentWidth: 0,
            showVerticalScrollBar: false,
            showHorizontalScrollBar: false,
            // columns
            treeColumns: [],
            flatColumns: [],
            maxColumnLevel: 0,
            // edit
            rowEditIndex: -1,
            // tooltips
            // tooltips
            tipProps: {
                offset: 20,
                content: "",
                reference: null,
                placement: "bottom",
                delay: 0,
                width: 300,
                options: {
                    modifiers: {
                        preventOverflow: {
                            boundariesElement: 'window'
                        }
                    },
                    eventsEnabled: false,
                }
            },
        }
    },
    provide() {
        return {
            treeRoot: this
        }
    },
    computed: {
        prefixCls() {
            return "ivu-tree"
        },

        localeNoDataText() {
            if (typeof this.noDataText === 'undefined') {
                return this.t('i.tree-table.noDataText')
            } else {
                return this.noDataText
            }
        },

        // 中央内容区域高度
        contentHeight() {
            if (this.height == "auto") {
                return this.height
            }

            // 2为table的border高度
            if (this.showHeader) {
                return this.height > this.headerHeight + 2 ? this.height - this.headerHeight - 2 + 'px' : 0
            }
            
            return this.height + 'px'
        },

        // 中央内容区样式
        contentStyle() {
            return {
                width: this.tableWidth + "px",
                height: this.contentHeight,
                overflow: 'auto'
            }
        },

        fixedContentHeight() {
            if (this.contentHeight != "auto" && this.showHorizontalScrollBar) {
                return parseFloat(this.contentHeight) - this.scrollBarWidth + "px"
            }

            return this.contentHeight
        },

        leftColumns() {
            return this.flatColumns.filter(c => c.fixed == "left")
        },

        leftContentStyle() {
            return {
                height: this.fixedContentHeight,
                overflow: "hidden",
            }
        },

        rightColumns() {
            return this.flatColumns.filter(c => c.fixed == "right")
        },

        rightContentStyle() {
            return {
                height: this.fixedContentHeight,
                overflow: "hidden",
            }
        },

        // 可汇总列
        accColumns() {
            return this.flatColumns.filter(v => v._isEnd && v.accUp)
        },

        // override table-formula.flatData
        flatData() {
            return this.flatState
        },

        // override table-formula.columnKeys
        columnKeys() {
            return this.flatColumns.filter(v => v._isEnd).map(v => v.key)
        },

        // 无数据图片样式
        noDataImgStyle() {
            var style = {
                width: "100%",
                height: "100%",
                lineHeight: this.contentHeight,
                textAlign: "center"
            }

            if (this.noDataImg) {
                style.background = `url(${this.noDataImg}) no-repeat center`
            }

            return style
        },
    },
    methods: {
        // 建立node的索引数组
        compileFlatState() {
            let keyCounter = 0,
                childrenKey = this.childrenKey,
                lastNodeEdited = null,
                lastHighlightNode = null,
                me = this

            const flatTree = []

            function flattenChildren(data, parent, level) {
                data.nodeKey = keyCounter++

                // 内部树节点
                var node = {
                    nodeKey: data.nodeKey,
                    node: data,
                    data: data,
                    parentNode: null, // parentNode
                    parent: null, // parentKey
                    children: [], // child nodes
                    level,
                    isEnd: true,
                    _isTreeNode: true, // 配合table-track、table-formula，后续考虑移除
                    _isHover: false, // 行hover
                    _isHighlight: false, // 是否高亮（current row）
                    _isEditing: false, // 是否可编辑
                    _isSelected: data.selected || false, // 是否单击选中
                    _isChecked: data.checked || false, // 是否勾选
                    _isDisableCheckbox: data.disableCheckbox || false, // 是否禁用勾选
                    _isIndeterminate: false, // 是否半勾选
                    _isExpand: data.expand || false, // 是否展开
                    _isDisabled: data.disabled || false, // 是否禁用check、select
                    _isLoading: typeof data.loading !== "undefined" ? data.loading : void(0), // 是否处于加载状态（异步加载）
                }

                flatTree.push(node)

                // 只读模式
                if (me.editMode == "readonly") {
                    node._isEditing = false
                }

                // 全网格编辑
                if (me.editMode == "grid") {
                    node._isEditing = true
                }

                // 行编辑，最后一个editing生效
                if (me.editMode == "row") {
                    node._isEditing = !!node._editing

                    if (data._editing) {
                        if (lastNodeEdited) {
                            lastNodeEdited._isEditing = false
                        }

                        lastNodeEdited = node
                    }
                }

                // 高亮初始化，最后一个_highlight有效
                if (me.highlightRow) {
                    node._isHighlight = !!data._highlight

                    if (data._highlight) {
                        if (lastHighlightNode) {
                            lastHighlightNode._isHighlight = false
                        }

                        lastHighlightNode = node
                    }
                }

                // 存在父级
                if (parent) {
                    node.parent = parent.nodeKey
                    node.parentNode = flatTree[parent.nodeKey]
                    node.parentNode.children.push(node)
                    node.parentNode.isEnd = false
                }

                // 递归处理子级数据
                if (data[childrenKey]) {
                    data[childrenKey].forEach(child => flattenChildren(child, data, level + 1))
                }
            }

            // 构建平级结构
            this.stateTree.forEach(root => {
                flattenChildren(root, null, 0)
            })

            // 不存在可编辑行
            if (!lastNodeEdited) {
                this.rowEditIndex = -1
            } else {
                this.editRow(lastNodeEdited.nodeKey)
            }

            this.flatState = flatTree

            return flatTree
        },

        // 获取分组表头
        compileColumns() {
            var list = [],
                me = this,
                id = 1

            // 深度遍历
            function deep(cb, tree, parent) {
                tree.forEach(v => {
                    var p = cb.call(me, v, parent)

                    if (v.children) {
                        deep(cb, v.children, p)
                    }
                })
            }

            // 处理level
            deep(function(column, parent) {
                var node = deepCopy(column)

                Object.assign(node, {
                    _parent: parent,
                    _children: [],
                    _id: id++,
                    _level: 0,
                    _isEnd: true,
                    _colspan: 0,
                    _rowspan: 0,
                    _originWidth: node.width,
                    width: node.width,
                    fixed: node.fixed || 'middle',
                    indent: node.indent || false,
                })

                if (parent) {
                    node._level = parent._level + 1
                    // todo 
                    // 目前只支持在顶层column设置fixed，所以子级fixed跟随父级
                    // 之后，若支持任意层级设置fixed，则需要将fixed映射到最底层column，并补充结构上缺少的父级column
                    parent._isEnd = false
                    parent._children.push(node)
                }

                // map[node._id] = node
                list.push(node)
                // stacks[node._level] ? stacks[node._level].push(node) : (stacks[node._level] = [node])

                if (this.maxColumnLevel < node._level) {
                    this.maxColumnLevel = node._level
                }

                return node
            }, this.columns)

            // 计算colspan, rowspan
            list.filter(v => v._isEnd)
                .forEach(v => {
                    v._rowspan = this.maxColumnLevel - v._level + 1
                    v._colspan = 1

                    var parent = v._parent
                    while (parent) {
                        parent._rowspan = 1
                        parent._colspan++

                        parent = parent._parent
                    }
                })

            // 列排序
            this.treeColumns = list.filter(v => !v._parent)
                .sort((c1, c2) => {
                    var f1 = c1.fixed,
                        f2 = c2.fixed

                    if (f1 < f2) {
                        return -1
                    }

                    if (f1 > f2) {
                        return 1
                    }

                    if (f1 == f2) {
                        return c1.id - c2.id
                    }
                })

            // 平级列
            this.flatColumns = list
        },

        rebuildTree() {
            const checkedNodes = this.getCheckedNodes()

            if (this.isolation) {
                return
            }

            checkedNodes.forEach(node => {
                // 向下递归勾选子节点
                this.updateTreeDown(node, { _isChecked: true })

                // 向上处理父节点勾选状态
                const parentNode = node.parentNode
                // update tree upwards
                if (parentNode && node._isChecked && parentNode._isChecked != node._isChecked) {
                    this.updateTreeUp(node)
                }
            })
        },

        resize() {
            console.log("resize: " + "prev: " + this.tableWidth + " new: " + (this.$el.offsetWidth - 2))
            this.tableWidth = this.$el.offsetWidth - 2

            // 提取末级column, 计算列宽
            var endColumns = this.flatColumns.filter(v => v._isEnd),
                noShrinkWidth = 0,
                noShrinkCols = [],
                flexibleCols = [],
                indentCol = null // 缩进列

            // 列宽计算
            endColumns.forEach(col => {
                // 先还原
                col.width = col._originWidth
                noShrinkWidth += col.width ? col.width : 0

                if (typeof col.width != "undefined") {
                    noShrinkCols.push(col)
                } else {
                    flexibleCols.push(col)
                }

                if (!indentCol && col.indent) {
                    indentCol = col
                }
            })

            // 均分空闲宽度
            function spreadAvailableWidth(availableWidth, columns) {
                if (columns.length > 0) {
                    if (availableWidth > 0) {
                        var width = availableWidth / columns.length

                        // 处理minWidth
                        for (var i = 0; i < columns.length; i++) {
                            var col = columns[i]

                            if (col.minWidth && col.minWidth > width) {
                                col.width = col.minWidth
                                columns.splice(i, 1)

                                return spreadAvailableWidth(availableWidth - col.width, columns)
                            }
                        }

                        // 处理maxWidth
                        for (var i = 0; i < columns.length; i++) {
                            if (col.maxWidth && col.maxWidth < width) {
                                col.width = col.maxWidth
                                columns.splice(i, 1)

                                return spreadAvailableWidth(availableWidth - col.width, columns)
                            }
                        }

                        // 处理null width
                        columns.forEach(col => col.width = width)
                    } else {
                        columns.forEach(col => {
                            if (col.minWidth) {
                                col.width = col.minWidth
                            } else {
                                col.width = col.maxWidth < 100 ? col.maxWidth : 100
                            }
                        })
                    }
                }
            }

            var availableWidth = this.tableWidth - noShrinkWidth - (this.showVerticalScrollBar ? this.scrollBarWidth : 0)
            console.log("showVerticalScrollBar ? " + this.showVerticalScrollBar + " availableWidth = " + availableWidth)
            spreadAvailableWidth(availableWidth, flexibleCols)

            indentCol = null // 缩进列
            // 设定默认缩进列
            if (!indentCol && endColumns.length > 0) {
                endColumns[0].indent = true
            }

            // calc rect
            this.contentWidth = endColumns.reduce((a, b) => a + b.width, 0)

            this.$nextTick(() => this.fixRect())
        },

        fixRect() {
            var $content = this.$refs.content

            if ($content) {
                // 是否出现滚动条
                this.showHorizontalScrollBar = $content && this.height != 'auto' && $content.clientWidth < $content.scrollWidth
                this.showVerticalScrollBar = $content && $content.clientHeight < $content.scrollHeight
            }
        },

        updateTreeDown(node, changes = {}) {
            for (let key in changes) {
                node[key] = changes[key]
            }

            node.children.forEach(child => this.updateTreeDown(child, changes))
        },

        updateTreeUp(node) {
            var parentNode = node.parentNode

            // 父级不存在
            if (!parentNode) return
            // no need to update upwards
            if (node._isChecked == parentNode._isChecked && node._isIndeterminate == parentNode._isIndeterminate) return

            if (node._isChecked == true) {
                parentNode._isChecked = parentNode.children.every(c => c._isChecked)
                parentNode._isIndeterminate = !parentNode._isChecked
            } else {
                parentNode._isChecked = false
                parentNode._isIndeterminate = parentNode.children.some(c => c._isChecked || c._isIndeterminate)
            }

            this.updateTreeUp(parentNode)
        },

        handleSelect(nodeKey) {
            const node = this.flatState[nodeKey]
            if (!this.multiple) {
                const currentSelectedKey = this.flatState.findIndex(obj => obj._isSelected)

                // reset previously selected node
                if (currentSelectedKey >= 0 && currentSelectedKey !== nodeKey) {
                    this.flatState[currentSelectedKey]._isSelected = false
                }
            }

            node._isSelected = !node._isSelected
            this.$emit('on-select-change', this.getSelectedNodes())
        },

        handleCheck({ _isChecked, nodeKey }) {
            const node = this.flatState[nodeKey]
            node._isChecked = _isChecked
            node._isIndeterminate = false

            if (this.isolation == false) {
                this.updateTreeUp(node); // propagate up
                this.updateTreeDown(node, { _isChecked, _isIndeterminate: false }); // reset `_isIndeterminate` when going down    
            }

            this.$emit('on-check-change', this.getCheckedNodes())
        },

        handleExpand(node) {
            this.$emit('on-toggle-expand', node)
            // 跳过折叠动效
            setTimeout(() => this.resize(), 300)
        },

        handleRowClick(node) {
            var index = node.nodeKey

            // 行编辑模式开启编辑
            if (this.editMode == "row") {
                if (index != this.rowEditIndex) {
                    var last = this.flatState[this.rowEditIndex]

                    // 编辑行
                    this.editRow(index)
                }
            }

            // 高亮显示
            this.highlightCurrentRow(index)
            // row-click
            this.$emit('on-row-click', this.flatState[index], index)
        },

        // errorTip 提示框操控方法
        handleShowPopper(config) {
            Object.assign(this.tipProps, config)
            this.$refs.cellTooltip.handleShowPopper()
        },

        handleClosePopper() {
            this.$refs.cellTooltip.handleClosePopper()
        },

        handleBodyScroll(event) {
            var header = this.$refs.header,
                leftContent = this.$refs.leftContent,
                rightContent = this.$refs.rightContent

            if (header) header.$el.scrollLeft = event.target.scrollLeft
            if (leftContent) leftContent.scrollTop = event.target.scrollTop
            if (rightContent) rightContent.scrollTop = event.target.scrollTop
            // this.hideColumnFilter();
        },

        handleFixedBodyMousewheel(event) {
            let deltaY = event.deltaY
            if (!deltaY && event.detail) {
                deltaY = event.detail * 40
            }
            if (!deltaY && event.wheelDeltaY) {
                deltaY = -event.wheelDeltaY
            }
            if (!deltaY && event.wheelDelta) {
                deltaY = -event.wheelDelta
            }
            if (!deltaY) return

            // 中央滚动区域
            const content = this.$refs.content
            const currentScrollTop = content.scrollTop

            if (deltaY < 0 && currentScrollTop !== 0) {
                event.preventDefault()
            }

            if (deltaY > 0 && content.scrollHeight - content.clientHeight > currentScrollTop) {
                event.preventDefault()
            }

            let step = 0
            let timeId = setInterval(() => {
                step += 5
                if (deltaY > 0) {
                    content.scrollTop += 2
                } else {
                    content.scrollTop -= 2
                }

                if (step >= Math.abs(deltaY)) {
                    clearInterval(timeId)
                }
            }, 5)
        },

        // 向上增量汇总
        accUp(node, changes, flatState, callback) {
            flatState = flatState || this.flatState
            var parent = flatState[node.parent]

            if (parent) {
                var pNode = parent.node

                for (let key in changes) {
                    var item = changes[key]

                    // 最大层级限制
                    if (item.minLevel > parent.level) {
                        continue
                    }

                    pNode[key] = (parseFloat(pNode[key] || 0) + parseFloat(item.increment || 0)).toFixed(item.decimalLimit)

                    if (callback) {
                        callback.call(this, parent)
                    }
                }
                this.accUp(parent, changes, flatState, callback)
            }
        },

        // 层级汇总
        summaryUp() {
            // 初始化汇总时间戳
            var summaryTime = now()

            // 父级清空
            this.flatState.filter(v => !v.isEnd).forEach(v => {
                this.accColumns.forEach(col => v.node[col.key] = 0)
            })

            // 子级累加
            this.flatState.filter(v => v.isEnd).forEach(v => {
                var hasIncrement = false,
                    increments = this.accColumns.reduce((memo, col) => {
                        var decimalLimit = 2

                        if (col.options && col.options.props) {
                            decimalLimit = col.options.props.decimalLimit || 2
                        }

                        memo[col.key] = {
                            increment: v.node[col.key],
                            decimalLimit
                        }

                        if (parseFloat(v.node[col.key]) != 0) {
                            hasIncrement = true
                        }

                        return memo
                    }, {})

                if (hasIncrement) {
                    this.accUp(v, increments, this.flatState, pNode => pNode._summaryTime = summaryTime)
                }
            })

            var accUpChangedNodes = this.flatState.filter(v => v._summaryTime == summaryTime && v.isEnd == false)

            if (accUpChangedNodes.length > 0) {
                this.$emit("accUpChange", accUpChangedNodes)
            }
        },

        editRow(index, flatState) {
            if (this.editMode != "row") {
                return
            }

            var cancel = false,
                list = flatState || this.flatState,
                e = {
                    row: list[index],
                    lastRow: list[this.rowEditIndex],
                    preventDefault: function() {
                        cancel = true
                    }
                }

            // 触发事件
            if (e.row) {
                this.$emit("beforeEditRow", e)
            }

            // 取消编辑
            if (cancel) {
                if (e.row) {
                    e.row._isEditing = false
                }
            } else {
                if (e.row) {
                    e.row._isEditing = true
                }

                if (e.lastRow) {
                    e.lastRow._isEditing = false
                }

                this.rowEditIndex = index
            }
        },

        highlightCurrentRow(index) {
            // 去重
            if (!this.highlightRow || this.flatState[index]._isHighlight) return

            // 找到并切换old
            var old = this.flatState.find(v => v._isHighlight),
                oldData = null,
                newData = null

            if (old) {
                old._isHighlight = false
                oldData = this.flatState[old.nodeKey]
            }

            if (index >= 0) {
                this.flatState[index]._isHighlight = true
                newData = this.flatState[index]
            }

            this.$emit('on-current-change', newData, oldData)
        },

        clearCurrentRow() {
            if (!this.highlightRow) return

            this.highlightCurrentRow(-1)
        },

        // 复制node数据
        copyNode(nodeKey) {
            return JSON.parse(JSON.stringify(this.flatState[nodeKey], (key, value) => {
                // parentNode会导致循环引用，替换为true/false
                if (key == "parentNode") {
                    return !!value
                }

                // children会导致数据量过大的问题，屏蔽掉
                if (key == "children") {
                    return value.length > 0
                }

                return value
            }))
        },

        setDefaultExpandLevel() {
            this.flatState.forEach(v => {
                if (typeof v.level == "number" && typeof v.data.expand == "undefined") {
                    if (v.level <= this.expandLevel) {
                        v._isExpand = true
                    }
                }
            })
        },

        getSelectedNodes() {
            /* public API */
            return this.flatState.filter(obj => obj._isSelected)
        },

        getCheckedNodes() {
            /* public API */
            return this.flatState.filter(obj => obj._isChecked)
        },

        getCheckedAndIndeterminateNodes() {
            /* public API */
            return this.flatState.filter(obj => (obj._isChecked || obj._isIndeterminate))
        },

        // table-export.headers:
        exportHeaders(){
            return this.treeColumns
        },

        // table-export.rows:
        exportRows(){
            return this.data
        },
    },
    watch: {
        data(newVal, oldVal) {
            this.stateTree = newVal
            this.compileFlatState()
            this.summaryUp()
            this.formulaParse()
            this.rebuildTree()

            if (newVal !== oldVal) {
                this.setDefaultExpandLevel()
                this.initTrack()
            }

            this.resize()
        },

        columns: {
            handler(newVal, oldVal) {
                this.compileColumns()
                this.summaryUp()
                this.formulaParse()
                this.resize()
            },
            deep: true
        },

        editMode() {
            this.compileFlatState()
        },

        height() {
            this.resize()
        },

        rowEditIndex() {
            // 编辑单元格时，隐藏tooltip提示，避免干扰
            this.handleClosePopper()
        }
    },
    created() {
        var me = this

        this.compileColumns()
        this.compileFlatState()
        this.summaryUp()
        this.formulaParse()
        this.rebuildTree()
        this.setDefaultExpandLevel()
        this.initTrack()
    },
    mounted() {
        this.windowResize = debounce(50, this.resize)
        this.containerResize = debounce(50, () => {
            var oldWidth = this.tableWidth,
                newWidth = this.$el.offsetWidth - 2

            if (oldWidth != newWidth) {
                this.resize()
            }
        })

        // 尺寸调整
        this.resize()

        // 监视窗口大小
        on(window, 'resize', this.windowResize)
        this.observer = elementResizeDetectorMaker()
        this.observer.listenTo(this.$el, this.containerResize)

        // 监视滚动条
        this.$watch(function() {
            return this.showHorizontalScrollBar + '' + this.showVerticalScrollBar
        }, this.windowResize)
    },
    beforeDestroy() {
        off(window, 'resize', this.windowResize)
        this.observer.removeListener(this.$el, this.containerResize)
    },

    // 默认选项
    defaults
}
</script>